/** Persian digits (Extended Arabic-Indic, U+06F0–U+06F9) indexed by their ASCII value. */
const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;

/** Persian thousands separator (U+066C ARABIC THOUSANDS SEPARATOR). */
export const PERSIAN_GROUP_SEPARATOR = '٬';

/** Persian decimal separator (U+066B ARABIC DECIMAL SEPARATOR). */
export const PERSIAN_DECIMAL_SEPARATOR = '٫';

/** Replaces every ASCII digit with its Persian form. Other characters are kept. */
export function toPersianDigits(value: string | number): string {
  return String(value).replace(/[0-9]/g, (digit) => PERSIAN_DIGITS[Number(digit)] ?? digit);
}

/**
 * Normalizes Persian (U+06F0–U+06F9) and Arabic-Indic (U+0660–U+0669) digits to ASCII digits.
 * Used for every user-typed number, OTP, and code before it is validated or sent (FTD §3.3).
 */
export function normalizeDigits(value: string): string {
  return value.replace(/[۰-۹٠-٩]/g, (digit) => {
    const code = digit.charCodeAt(0);

    return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660);
  });
}

/** Groups an ASCII digit string in threes with the Persian separator and Persian digits. */
export function groupDigits(asciiDigits: string): string {
  const grouped = asciiDigits.replace(/\B(?=(\d{3})+(?!\d))/g, PERSIAN_GROUP_SEPARATOR);

  return toPersianDigits(grouped);
}

/** Formats a non-negative integer count (not money) with Persian digits and grouping. */
export function formatCount(value: number): string {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError('formatCount expects a safe integer.');
  }

  const sign = value < 0 ? '‎−' : '';

  return `${sign}${groupDigits(String(Math.abs(value)))}`;
}
